/* eslint-disable jsx-a11y/no-static-element-interactions */
import React, { useEffect, useReducer } from "react";
import { getUserSolutions } from "./MenuOptions";
import SolutionsMenu from "./SolutionsMenu";
import { Avatar } from "primereact/avatar";
import VelocityAccelerateLogo from "../../../assets/images/V_AccelerateLogo.png";
import VelocityAccelerateLogoSmall from "../../../assets/images/Velocity_V_Icon_resize.png";
import classNames from "classnames/bind";
import classes from "./SolutionsNavigation.module.scss";
import { useTranslation } from "react-i18next";
import { Button } from "primereact/button";
import { useHistory } from "react-router";
import { NavigationReducer } from "./NavigationReducer";
import FooterAnimationWrapper from "./FooterAnimationWrapper";
import UsersService from "@velocity-ehs/user-utils-module";
import { useFlags } from "launchdarkly-react-client-sdk";

type SolutionsNavigationProps = {
  hideItem?: Array<"notifications" | "translations">;
};

export enum FooterMenus {
  CustomerSwitcher = "CustomerSwitcher",
  AdminSettings = "AdminSettings",
  Translations = "Translations",
  UserProfile = "UserProfile",
}

interface ITileClickMessage {
  solutionId: string;
}

const SolutionsNavigation = ({
  hideItem = [],
}: SolutionsNavigationProps): React.ReactElement => {
  const { t } = useTranslation();
  const cx = classNames.bind(classes);
  const history = useHistory();
  const { user, permissions } = UsersService;
  const featureFlags = useFlags();

  const [state, dispatch] = useReducer(NavigationReducer, {
    isExpanded: true,
    solutions: [],
    openFooterMenu: null,
  });

  const handleTileClick = (message: ITileClickMessage) => {
    dispatch({ type: "tile_click", solutionId: message.solutionId });
  };

  useEffect(() => {
    UsersService.subscribe("TILE_CLICK", handleTileClick);

    return () => {
      UsersService.unsubscribe("TILE_CLICK", handleTileClick);
    };
  }, []);

  const toggleFooterMenus = (selectedMenu: FooterMenus) => {
    if (selectedMenu === state.openFooterMenu)
      return dispatch({ type: "close_footer" });
    return dispatch({
      type: "open_footer",
      footerMenu: selectedMenu,
    });
  };

  useEffect(() => {
    if (!user.assignedSolutions) return;
    const userSolutions = getUserSolutions(history, dispatch, featureFlags);
    dispatch({ type: "assign", solutions: userSolutions });
    // eslint-disable-next-line
  }, [user.assignedSolutions, featureFlags]);

  const goHome = () => history.push("/");

  return (
    <div
      className={cx(classes.navContainer, {
        opened: state.isExpanded,
        collapsed: !state.isExpanded,
      })}
    >
      <div className={classes.main}>
        <div className={cx(classes.collapseContainer)}>
          <i
            className={cx(classes.icon, "pi pi-angle-right", {
              opened: state.isExpanded,
            })}
            role="button"
            tabIndex={0}
            onClick={() =>
              dispatch({ type: state.isExpanded ? "collapse" : "expand" })
            }
          />
        </div>
        <div className={classes.brandingContainer}>
          {state.isExpanded ? (
            <div className={classes.logo} onClick={goHome}>
              <img
                src={VelocityAccelerateLogo}
                alt={t("solutions_navigation_home_logo_alt_text")}
              />
            </div>
          ) : (
            <div className={classes.logoSmall} onClick={goHome}>
              <img
                src={VelocityAccelerateLogoSmall}
                alt={t("solutions_navigation_home_logo_alt_text")}
              />
            </div>
          )}
        </div>

        {hideItem.includes("notifications") ? null : (
          <div className={classes.notificationContainer}>
            <div
              className={cx(classes.menuItemRow, {
                opened: state.isExpanded,
                collapsed: !state.isExpanded,
              })}
            >
              <Avatar label="99" shape="circle" />
              <div className={classes.notificationText}>
                <div
                  className={cx(classes.menuItem, {
                    menuItemDisplayed: state.isExpanded,
                    menuItemNotDisplayed: !state.isExpanded,
                  })}
                >
                  {t("solutions_navigation_notifications")}
                </div>
              </div>
            </div>
          </div>
        )}
        <SolutionsMenu items={state.solutions} navExpanded={state.isExpanded} />
      </div>
      <div className={classes.footer}>
        <FooterAnimationWrapper openFooterMenu={state.openFooterMenu} />
        <div
          className={cx(classes.footerOptions, {
            collapsed: !state.isExpanded,
          })}
        >
          {hideItem.includes("translations") ? null : (
            <Button
              icon="fa fa-flag"
              className={cx("p-button-text", {
                [classes.activeFooter]:
                  state.openFooterMenu === FooterMenus.Translations,
              })}
              aria-label={t("solutions_navigation_translations_title")}
              onClick={() => toggleFooterMenus(FooterMenus.Translations)}
            />
          )}
          <Button
            icon="fa fa-user"
            className={cx("p-button-text", {
              [classes.activeFooter]:
                state.openFooterMenu === FooterMenus.UserProfile,
            })}
            aria-label={t("solutions_navigation_my_profile_title")}
            onClick={() => toggleFooterMenus(FooterMenus.UserProfile)}
          />
          {permissions.canViewAdminMenu && (
            <Button
              icon="fa fa-gear"
              className={cx("p-button-text", {
                [classes.activeFooter]:
                  state.openFooterMenu === FooterMenus.AdminSettings,
              })}
              aria-label={t("solutions_navigation_admin_settings_title")}
              onClick={() => toggleFooterMenus(FooterMenus.AdminSettings)}
            />
          )}
          {permissions.canViewCustomers && (
            <Button
              icon="fa fa-layer-group"
              className={cx("p-button-text", {
                [classes.activeFooter]:
                  state.openFooterMenu === FooterMenus.CustomerSwitcher,
              })}
              aria-label={t("solutions_navigation_company_switcher_title")}
              onClick={() => toggleFooterMenus(FooterMenus.CustomerSwitcher)}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export type { SolutionsNavigationProps };
export default SolutionsNavigation;
